import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Badge,
  Button,
  Menu,
  MenuItem,
  Divider
} from '@mui/material';
import {
  Notifications as NotificationIcon,
  CheckCircle as ReadIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  TrendingUp as TransitionIcon,
  Assessment as PerformanceIcon,
  Inventory as AssetIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import hybridSocket from '../../../../offline/hybridSocket';

const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [backendUnreadCount, setBackendUnreadCount] = useState(0);
  
  // Get current user ID from localStorage
  const currentUserId = localStorage.getItem('userId');
  const navigate = useNavigate();

  // Helper function to personalize notification message
  const personalizeMessage = (notification) => {
    const isForCurrentUser = notification.employeeId?._id === currentUserId;
    const employeeName = notification.employeeId?.staff_name || 'Employee';
    const employeeId = notification.employeeId?.emp_id || '';
    const employeeDisplay = employeeId ? `${employeeName} (${employeeId})` : employeeName;
    
    // If notification is for current user, replace employee name with "you"
    if (isForCurrentUser) {
      // Replace "EmployeeName (EMP123)'s" with "Your"
      let message = notification.message.replace(new RegExp(`${employeeDisplay.replace(/[()]/g, '\\$&')}'s`, 'gi'), 'Your');
      // Replace "to EmployeeName (EMP123)" with "to you"
      message = message.replace(new RegExp(`to ${employeeDisplay.replace(/[()]/g, '\\$&')}`, 'gi'), 'to you');
      // Replace "for EmployeeName (EMP123)" with "for you"
      message = message.replace(new RegExp(`for ${employeeDisplay.replace(/[()]/g, '\\$&')}`, 'gi'), 'for you');
      return message;
    }
    
    // If notification is for someone else, keep the employee name and ID as-is
    return notification.message;
  };

  const handlePrimaryAction = async (notification) => {
    if (!notification) return;

    // First: always try to mark as read so user isn't confused
    try {
      console.log('🔔 NotificationCenter: Row clicked, marking as read:', notification._id, 'current status:', notification.status);
      await axios.post(`/api/employee-notification/read/${notification._id}`);
      setNotifications((prev) => {
        if (!Array.isArray(prev)) return prev;

        const updated = prev.map((n) =>
          n._id === notification._id ? { ...n, status: 'READ' } : n
        );

        return updated;
      });
      await syncUnreadCountFromBackend();
    } catch (error) {
      console.error('Error marking notification as read from primary action:', error);
    }

    const relatedModule = notification.relatedModule || notification.relatedEntity?.entityType;
    const type = notification.notificationType;

    // KOT / ongoing-order notifications: navigate to POS ongoing orders (heldOrder tab)
    if (relatedModule === 'KOT' || type === 'KOT_READY') {
      navigate('/userdashboard/sales/billingnew', {
        state: { fromNotification: true, tab: 'heldOrder' }
      });
      return;
    }

    // Other POS/BILLING notifications (e.g., add order) should NOT navigate – just mark as read.
    // Fall through without navigation for these.
    if (relatedModule === 'POS' || relatedModule === 'BILLING' || type === 'POS_ORDER') {
      return;
    }

    // Next priority: explicit actionUrl from backend (non-POS modules like Accounts, HR, etc.)
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      return;
    }

    // Performance review
    if (relatedModule === 'PERFORMANCE' || type === 'PERFORMANCE_REVIEW') {
      navigate('/userdashboard/staff/hrm/performance/review/list');
      return;
    }

    // PIP
    if (relatedModule === 'PIP' || type === 'PIP_ALERT') {
      navigate('/userdashboard/staff/hrm/performance/pip');
      return;
    }

    // Goals
    if (relatedModule === 'GOAL') {
      navigate('/userdashboard/staff/hrm/performance/goals');
      return;
    }

    // Asset notifications
    if (relatedModule === 'ASSET' || notification.relatedEntity?.entityType === 'ASSET' || (type && type.startsWith('ASSET_'))) {
      navigate('/userdashboard/staff/hrm/assets');
      return;
    }

    // Employee transitions (increment / promotion)
    if (relatedModule === 'TRANSITION' || type === 'INCREMENT' || type === 'PROMOTION') {
      navigate('/userdashboard/staff/hrm/employee-transitions');
      return;
    }

    // Fallback: go to Notification Center root (already here) – no-op
  };

  const getActionLabel = (notification) => {
    if (!notification) return 'View';

    const relatedModule = notification.relatedModule || notification.relatedEntity?.entityType;
    const type = notification.notificationType;

    if (
      relatedModule === 'POS' ||
      relatedModule === 'BILLING' ||
      relatedModule === 'KOT' ||
      type === 'POS_ORDER' ||
      type === 'KOT_READY'
    ) {
      return 'View Order';
    }

    if (relatedModule === 'PERFORMANCE' || type === 'PERFORMANCE_REVIEW') {
      return 'View Review';
    }
    if (relatedModule === 'PIP' || type === 'PIP_ALERT') {
      return 'View PIP';
    }
    if (relatedModule === 'GOAL') {
      return 'View Goal';
    }
    if (relatedModule === 'ASSET' || notification.relatedEntity?.entityType === 'ASSET' || (type && type.startsWith('ASSET_'))) {
      return 'View Asset';
    }
    if (relatedModule === 'TRANSITION' || type === 'INCREMENT' || type === 'PROMOTION') {
      return 'View Transition';
    }
    if (notification.actionUrl) {
      return 'Open';
    }
    if (notification.actionRequired) {
      return notification.actionType || 'Review';
    }
    return 'View';
  };

  // Helper: sync unread count from backend and emit via socket
  const syncUnreadCountFromBackend = async () => {
    try {
      const unreadResponse = await axios.post('/api/employee-notification/list', {
        status: 'UNREAD',
        page: 1,
        limit: 1
      });
      const unreadCount = unreadResponse.data?.pagination?.total ?? 0;
      console.log('🔔 NotificationCenter: Syncing unread count from backend:', unreadCount);
      setBackendUnreadCount(unreadCount); // Store backend count for button visibility
      hybridSocket.emit('notificationUnreadCountUpdated', { count: unreadCount });
    } catch (error) {
      console.error('Error syncing unread notification count from backend:', error);
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/employee-notification/list', {
        status: activeTab === 'unread' ? 'UNREAD' : undefined,
        page: 1,
        limit: 50
      });
      if (response.data?.success) {
        const list = response.data.data || [];
        setNotifications(list);
        await syncUnreadCountFromBackend();
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [activeTab]);

  // Realtime listener: when unread count is updated (via socket/hybrid),
  // refetch the list so NotificationCenter stays in sync without polling.
  useEffect(() => {
    const handleUnreadUpdate = () => {
      fetchNotifications();
    };

    hybridSocket.on('notificationUnreadCountUpdated', handleUnreadUpdate);

    return () => {
      hybridSocket.off('notificationUnreadCountUpdated', handleUnreadUpdate);
    };
  }, [activeTab]);

  // Mark as read
  const handleMarkAsRead = async (id) => {
    try {
      await axios.post(`/api/employee-notification/read/${id}`);
      setNotifications((prev) => {
        if (!Array.isArray(prev)) return prev;

        const updated = prev.map((n) =>
          n._id === id ? { ...n, status: 'READ' } : n
        );

        return updated;
      });
      await syncUnreadCountFromBackend();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await axios.post('/api/employee-notification/read-all');
      Swal.fire('Success', 'All notifications marked as read', 'success');
      await fetchNotifications();
      await syncUnreadCountFromBackend();
    } catch (error) {
      console.error('Error marking all as read:', error);
      Swal.fire('Error', 'Failed to mark notifications as read', 'error');
    }
  };

  // Delete notification
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Notification?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Delete'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/employee-notification/${id}`);
        Swal.fire('Deleted!', 'Notification deleted successfully', 'success');

        setNotifications((prev) => {
          if (!Array.isArray(prev)) return prev;

          const updated = prev.filter((n) => n._id !== id);

          return updated;
        });
        await syncUnreadCountFromBackend();
      } catch (error) {
        console.error('Error deleting notification:', error);
        Swal.fire('Error', 'Failed to delete notification', 'error');
      }
    }
  };

  // Get severity icon
  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'INFO':
        return <InfoIcon color="info" />;
      case 'WARNING':
        return <WarningIcon color="warning" />;
      case 'CRITICAL':
        return <ErrorIcon color="error" />;
      default:
        return <NotificationIcon />;
    }
  };

  // Get entity icon
  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case 'TRANSITION':
        return <TransitionIcon />;
      case 'PERFORMANCE_REVIEW':
      case 'PIP':
        return <PerformanceIcon />;
      case 'ASSET':
        return <AssetIcon />;
      default:
        return <NotificationIcon />;
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    const colors = {
      LOW: 'default',
      MEDIUM: 'info',
      HIGH: 'warning',
      URGENT: 'error'
    };
    return colors[priority] || 'default';
  };

  const unreadCount = notifications.filter(n => n.status === 'UNREAD').length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <NotificationIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Notifications
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {backendUnreadCount} unread notification{backendUnreadCount !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {backendUnreadCount > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleMarkAllAsRead}
            >
              Mark All as Read ({backendUnreadCount})
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
        >
          <Tab
            label={
              <Badge badgeContent={backendUnreadCount} max={9999} color="primary">
                All
              </Badge>
            }
            value="all"
          />
          <Tab
            label={
              <Badge badgeContent={backendUnreadCount} max={9999} color="error">
                Unread
              </Badge>
            }
            value="unread"
          />
        </Tabs>
      </Paper>

      {/* Notification List */}
      <Paper>
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">Loading notifications...</Typography>
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <NotificationIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              No notifications
            </Typography>
            <Typography variant="body2" color="textSecondary">
              You're all caught up!
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification._id}>
                <ListItem
                  sx={{
                    bgcolor: notification.status === 'UNREAD' ? 'action.hover' : 'transparent',
                    '&:hover': { bgcolor: 'action.selected' },
                    cursor: 'pointer'
                  }}
                  onClick={() => handlePrimaryAction(notification)}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {notification.status === 'UNREAD' && (
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification._id);
                          }}
                        >
                          <ReadIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAnchorEl(e.currentTarget);
                          setSelectedNotification(notification);
                        }}
                      >
                        <MoreIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: notification.status === 'UNREAD' ? 'primary.main' : 'grey.400' }}>
                      {notification.relatedEntity?.entityType 
                        ? getEntityIcon(notification.relatedEntity.entityType)
                        : getSeverityIcon(notification.severity)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                          variant="body1"
                          component="span"
                          fontWeight={notification.status === 'UNREAD' ? 600 : 400}
                        >
                          {notification.title}
                        </Typography>
                        <Chip
                          label={notification.priority}
                          size="small"
                          color={getPriorityColor(notification.priority)}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'block' }}>
                        <Typography variant="body2" component="span" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                          {personalizeMessage(notification)}
                        </Typography>
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                          <Typography variant="caption" component="span" color="textSecondary">
                            {new Date(notification.createdAt).toLocaleString()}
                          </Typography>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => handlePrimaryAction(notification)}
                          >
                            {getActionLabel(notification)}
                          </Button>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {selectedNotification?.status === 'UNREAD' && (
          <MenuItem
            onClick={() => {
              handleMarkAsRead(selectedNotification._id);
              setAnchorEl(null);
            }}
          >
            <ReadIcon fontSize="small" sx={{ mr: 1 }} />
            Mark as Read
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleDelete(selectedNotification._id);
            setAnchorEl(null);
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default NotificationCenter;
